/**
 * Abundance Tile Experience
 * Individual category experience with Bank, Vaults, Evidence, Future Letter
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowLeft, 
  Sparkles, 
  Heart, 
  Sun, 
  Home, 
  Compass, 
  Target,
  Plus,
  Check,
  Edit3,
  FileText,
  Droplets
} from 'lucide-react';

// Tile data mapping
const TILE_DATA = {
  wealth: {
    title: 'Wealth',
    phrase: 'It is safe for me to receive more',
    icon: Sparkles,
    confirmationLabel: 'Receipt',
    confirmationTitle: 'Paid in Full',
    color: '#CBA153',
    bgGradient: 'from-amber-950/40 to-[#121210]',
  },
  love: {
    title: 'Love',
    phrase: 'I am worthy of deep connection',
    icon: Heart,
    confirmationLabel: 'Connection',
    confirmationTitle: 'Connection Confirmed',
    color: '#E8A4A4',
    bgGradient: 'from-rose-950/40 to-[#121210]',
  },
  body: {
    title: 'Body',
    phrase: 'My body is my trusted companion',
    icon: Sun,
    confirmationLabel: 'Vitality',
    confirmationTitle: 'Vitality Confirmed',
    color: '#7BA378',
    bgGradient: 'from-emerald-950/40 to-[#121210]',
  },
  home: {
    title: 'Home',
    phrase: 'I belong in beautiful spaces',
    icon: Home,
    confirmationLabel: 'Residence',
    confirmationTitle: 'Residence Confirmed',
    color: '#A89080',
    bgGradient: 'from-stone-800/40 to-[#121210]',
  },
  freedom: {
    title: 'Freedom',
    phrase: 'I design my own path',
    icon: Compass,
    confirmationLabel: 'Liberation',
    confirmationTitle: 'Freedom Confirmed',
    color: '#7BA3B8',
    bgGradient: 'from-sky-950/40 to-[#121210]',
  },
  purpose: {
    title: 'Purpose',
    phrase: 'My work creates lasting impact',
    icon: Target,
    confirmationLabel: 'Mission',
    confirmationTitle: 'Purpose Confirmed',
    color: '#9B8AC4',
    bgGradient: 'from-violet-950/40 to-[#121210]',
  },
};

// Bank of Reclaim Component
const BankOfReclaim = ({ category, value, onChange }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [displayValue, setDisplayValue] = useState(value);
  const [inputValue, setInputValue] = useState(value.toString());

  useEffect(() => {
    // Animate number settling
    const target = value;
    const step = (target - displayValue) / 20;
    if (Math.abs(target - displayValue) > 1) {
      const timer = setTimeout(() => {
        setDisplayValue(displayValue + step);
      }, 30);
      return () => clearTimeout(timer);
    } else {
      setDisplayValue(target);
    }
  }, [value, displayValue]);

  const handleSave = () => {
    const numValue = parseFloat(inputValue.replace(/[^0-9.]/g, '')) || 0;
    onChange(numValue);
    setIsEditing(false);
  };

  const formatCurrency = (num) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(num);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7 }}
      className="bg-[#1A1A18] rounded-2xl p-8 border border-[#2C2C28]"
    >
      <div className="flex items-center justify-between mb-8">
        <div>
          <p className="text-[#9E9A90] text-xs uppercase tracking-[0.2em] font-light mb-1">
            Bank of Reclaim
          </p>
          <p className="text-[#F2EFE9] text-sm font-light">
            Your abundance reality
          </p>
        </div>
        <button
          onClick={() => setIsEditing(!isEditing)}
          className="p-2 rounded-lg hover:bg-[#2C2C28] transition-colors duration-300"
          data-testid="edit-bank"
        >
          <Edit3 size={18} strokeWidth={1.5} className="text-[#9E9A90]" />
        </button>
      </div>

      {isEditing ? (
        <div className="space-y-4">
          <input
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            className="w-full bg-[#121210] border border-[#2C2C28] rounded-xl px-6 py-4 text-3xl text-[#F2EFE9] font-['Playfair_Display'] text-center focus:outline-none focus:border-[#4A6741]/50"
            placeholder="Enter amount"
            autoFocus
            data-testid="bank-input"
          />
          <button
            onClick={handleSave}
            className="w-full py-3 rounded-xl bg-[#4A6741] text-[#F2EFE9] font-light hover:bg-[#5A7751] transition-colors duration-300"
            data-testid="save-bank"
          >
            Confirm
          </button>
        </div>
      ) : (
        <div className="text-center py-4">
          <motion.p
            key={displayValue}
            className="font-['Playfair_Display'] text-5xl md:text-6xl text-[#F2EFE9] tracking-tight"
          >
            {formatCurrency(Math.round(displayValue))}
          </motion.p>
          <div className="mt-4 pt-4 border-t border-[#2C2C28]">
            <p className="text-[#9E9A90] text-xs uppercase tracking-[0.15em]">
              Available Balance
            </p>
          </div>
        </div>
      )}
    </motion.div>
  );
};

// Abundance Vault Card
const VaultCard = ({ vault, onUpdate, color }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [name, setName] = useState(vault.name);
  const [amount, setAmount] = useState(vault.amount.toString());

  const handleSave = () => {
    onUpdate({ ...vault, name, amount: parseFloat(amount) || 0 });
    setIsEditing(false);
  };

  const progress = vault.current ? (vault.current / vault.amount) * 100 : 0;

  return (
    <motion.div
      layout
      className="bg-[#1A1A18] rounded-xl p-5 border border-[#2C2C28]"
    >
      {isEditing ? (
        <div className="space-y-3">
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full bg-[#121210] border border-[#2C2C28] rounded-lg px-4 py-2 text-[#F2EFE9] text-sm focus:outline-none"
            placeholder="Vault name"
          />
          <input
            type="text"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className="w-full bg-[#121210] border border-[#2C2C28] rounded-lg px-4 py-2 text-[#F2EFE9] text-sm focus:outline-none"
            placeholder="Target amount"
          />
          <button
            onClick={handleSave}
            className="w-full py-2 rounded-lg bg-[#4A6741] text-[#F2EFE9] text-sm"
          >
            Save
          </button>
        </div>
      ) : (
        <div onClick={() => setIsEditing(true)} className="cursor-pointer">
          <p className="text-[#F2EFE9] font-light mb-2">{vault.name}</p>
          <p className="font-['Playfair_Display'] text-xl text-[#F2EFE9]">
            £{vault.amount.toLocaleString()}
          </p>
          {vault.current && (
            <div className="mt-3">
              <div className="h-1 bg-[#2C2C28] rounded-full overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${progress}%` }}
                  transition={{ duration: 1, ease: "easeOut" }}
                  className="h-full rounded-full"
                  style={{ backgroundColor: color }}
                />
              </div>
            </div>
          )}
        </div>
      )}
    </motion.div>
  );
};

// Abundance Vaults Section
const AbundanceVaults = ({ vaults, setVaults, color }) => {
  const addVault = () => {
    const newVault = {
      id: Date.now(),
      name: 'New Goal',
      amount: 10000,
      current: 0,
    };
    setVaults([...vaults, newVault]);
  };

  const updateVault = (updatedVault) => {
    setVaults(vaults.map(v => v.id === updatedVault.id ? updatedVault : v));
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7, delay: 0.1 }}
    >
      <div className="flex items-center justify-between mb-4">
        <p className="text-[#9E9A90] text-xs uppercase tracking-[0.2em] font-light">
          Abundance Vaults
        </p>
        <button
          onClick={addVault}
          className="p-2 rounded-lg hover:bg-[#1A1A18] transition-colors duration-300"
          data-testid="add-vault"
        >
          <Plus size={18} strokeWidth={1.5} className="text-[#9E9A90]" />
        </button>
      </div>

      <div className="space-y-3">
        {vaults.map((vault) => (
          <VaultCard
            key={vault.id}
            vault={vault}
            onUpdate={updateVault}
            color={color}
          />
        ))}
      </div>
    </motion.div>
  );
};

// Evidence Board
const EvidenceBoard = ({ evidence, addEvidence }) => {
  const [isAdding, setIsAdding] = useState(false);
  const [newEvidence, setNewEvidence] = useState('');

  const handleAdd = () => {
    if (newEvidence.trim()) {
      addEvidence({
        id: Date.now(),
        text: newEvidence,
        date: new Date().toISOString(),
      });
      setNewEvidence('');
      setIsAdding(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7, delay: 0.2 }}
      className="bg-[#1A1A18] rounded-2xl p-6 border border-[#2C2C28]"
    >
      <div className="flex items-center justify-between mb-6">
        <div>
          <p className="text-[#9E9A90] text-xs uppercase tracking-[0.2em] font-light mb-1">
            Evidence of Abundance
          </p>
          <p className="text-[#F2EFE9] text-2xl font-['Playfair_Display']">
            {evidence.length} <span className="text-sm text-[#9E9A90] font-light">moments logged</span>
          </p>
        </div>
        <button
          onClick={() => setIsAdding(true)}
          className="p-2 rounded-lg hover:bg-[#2C2C28] transition-colors duration-300"
          data-testid="add-evidence"
        >
          <Plus size={18} strokeWidth={1.5} className="text-[#9E9A90]" />
        </button>
      </div>

      <AnimatePresence>
        {isAdding && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mb-4"
          >
            <textarea
              value={newEvidence}
              onChange={(e) => setNewEvidence(e.target.value)}
              className="w-full bg-[#121210] border border-[#2C2C28] rounded-xl px-4 py-3 text-[#F2EFE9] text-sm font-light focus:outline-none focus:border-[#4A6741]/50 resize-none"
              placeholder="What evidence of abundance did you notice today?"
              rows={3}
              autoFocus
              data-testid="evidence-input"
            />
            <div className="flex gap-2 mt-3">
              <button
                onClick={handleAdd}
                className="flex-1 py-2 rounded-lg bg-[#4A6741] text-[#F2EFE9] text-sm font-light"
              >
                Log Evidence
              </button>
              <button
                onClick={() => setIsAdding(false)}
                className="py-2 px-4 rounded-lg bg-[#2C2C28] text-[#9E9A90] text-sm"
              >
                Cancel
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Recent evidence timeline */}
      <div className="space-y-3 max-h-48 overflow-y-auto">
        {evidence.slice(-5).reverse().map((item) => (
          <div
            key={item.id}
            className="flex gap-3 py-2 border-b border-[#2C2C28]/50 last:border-0"
          >
            <div className="w-1.5 h-1.5 rounded-full bg-[#4A6741] mt-2 flex-shrink-0" />
            <div>
              <p className="text-[#F2EFE9] text-sm font-light">{item.text}</p>
              <p className="text-[#9E9A90] text-xs mt-1">
                {new Date(item.date).toLocaleDateString('en-GB', { 
                  day: 'numeric', 
                  month: 'short' 
                })}
              </p>
            </div>
          </div>
        ))}
      </div>
    </motion.div>
  );
};

// Future Letter Component
const FutureLetter = ({ letter, setLetter }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(letter);

  const handleSave = () => {
    setLetter(draft);
    setIsEditing(false);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7, delay: 0.3 }}
      className="bg-[#F7F5F0] rounded-2xl p-8 text-[#1A1A18]"
    >
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <FileText size={20} strokeWidth={1.5} className="text-[#6E6A60]" />
          <p className="text-[#6E6A60] text-xs uppercase tracking-[0.2em] font-light">
            Future Letter
          </p>
        </div>
        <button
          onClick={() => setIsEditing(!isEditing)}
          className="text-[#6E6A60] hover:text-[#1A1A18] transition-colors"
          data-testid="edit-letter"
        >
          <Edit3 size={16} strokeWidth={1.5} />
        </button>
      </div>

      {isEditing ? (
        <div className="space-y-4">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            className="w-full bg-white border border-[#E5E2DA] rounded-xl px-6 py-4 text-[#1A1A18] font-['Playfair_Display'] text-lg leading-relaxed focus:outline-none focus:border-[#4A6741]/50 resize-none"
            rows={6}
            placeholder="Dear future self..."
            data-testid="letter-input"
          />
          <button
            onClick={handleSave}
            className="w-full py-3 rounded-xl bg-[#4A6741] text-[#F2EFE9] font-light"
          >
            Save Letter
          </button>
        </div>
      ) : (
        <div className="min-h-[120px]">
          {letter ? (
            <p className="font-['Playfair_Display'] text-lg leading-relaxed text-[#1A1A18]">
              {letter}
            </p>
          ) : (
            <p className="text-[#9E9A90] italic font-light">
              Write a letter to your future self from this new reality...
            </p>
          )}
        </div>
      )}
    </motion.div>
  );
};

// Main Tile Experience Component
export const AbundanceTileExperience = () => {
  const { category } = useParams();
  const navigate = useNavigate();
  const tileData = TILE_DATA[category] || TILE_DATA.wealth;
  const Icon = tileData.icon;

  // State - would connect to backend/localStorage in production
  const [bankAmount, setBankAmount] = useState(250000);
  const [vaults, setVaults] = useState([
    { id: 1, name: 'Dream Home Deposit', amount: 100000, current: 25000 },
    { id: 2, name: 'Investment Portfolio', amount: 500000, current: 125000 },
  ]);
  const [evidence, setEvidence] = useState([
    { id: 1, text: 'Found £20 on the street', date: '2024-03-20' },
    { id: 2, text: 'Received unexpected refund', date: '2024-03-22' },
  ]);
  const [letter, setLetter] = useState('');

  const addEvidence = (newEvidence) => {
    setEvidence([...evidence, newEvidence]);
  };

  return (
    <div className={`min-h-screen bg-gradient-to-b ${tileData.bgGradient}`}>
      {/* Noise texture */}
      <div 
        className="fixed inset-0 pointer-events-none opacity-[0.03]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
        }}
      />

      {/* Header */}
      <header className="relative z-10 px-6 pt-6 pb-4">
        <div className="flex items-center justify-between">
          <button
            onClick={() => navigate('/abundance')}
            className="p-2 -ml-2 rounded-lg hover:bg-white/5 transition-colors duration-300"
            data-testid="back-button"
          >
            <ArrowLeft size={24} strokeWidth={1.5} className="text-[#F2EFE9]" />
          </button>
          
          {/* Bathe Button */}
          <motion.button
            onClick={() => navigate(`/abundance/${category}/bathe`)}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#4A6741]/20 border border-[#4A6741]/30 hover:bg-[#4A6741]/30 transition-colors duration-500"
            data-testid="bathe-button"
          >
            <Droplets size={18} strokeWidth={1.5} className="text-[#4A6741]" />
            <span className="text-[#F2EFE9] text-sm font-light">Bathe</span>
          </motion.button>
        </div>
      </header>

      {/* Title Section */}
      <div className="relative z-10 px-6 py-8">
        <div className="flex items-center gap-3 mb-3">
          <Icon size={20} strokeWidth={1.5} style={{ color: tileData.color }} />
          <span className="text-[#9E9A90] text-xs uppercase tracking-[0.2em] font-light">
            {tileData.title}
          </span>
        </div>
        <h1 className="font-['Playfair_Display'] text-3xl text-[#F2EFE9] leading-snug mb-2">
          "{tileData.phrase}"
        </h1>
      </div>

      {/* Main Content */}
      <main className="relative z-10 px-6 pb-24 space-y-6">
        {/* Bank of Reclaim */}
        <BankOfReclaim 
          category={category}
          value={bankAmount}
          onChange={setBankAmount}
        />

        {/* Abundance Vaults */}
        <AbundanceVaults 
          vaults={vaults}
          setVaults={setVaults}
          color={tileData.color}
        />

        {/* Evidence Board */}
        <EvidenceBoard 
          evidence={evidence}
          addEvidence={addEvidence}
        />

        {/* Future Letter */}
        <FutureLetter 
          letter={letter}
          setLetter={setLetter}
        />
      </main>
    </div>
  );
};

export default AbundanceTileExperience;
